import { useCallback, useMemo, useState } from 'react'

import { OptionsHelper, formatDateToPtBr, initialQueryState } from '_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  IOption,
  TableFilter,
  TableFilters,
  TextInput,
} from 'app/components'
import {
  getPacientes,
  getPacientesEmpresa,
} from '../../../../../paciente-management/pacientes-list/core/_requests'
import { getUnidades } from '../../../../../unidades/unidades-list/core/_requests'
import { useAuth } from '../../../../../../auth'
import {
  EsusEnvioFiltros,
  EsusEnvioStatus,
  EsusEnvioTipoFicha,
  OPCOES_STATUS_ENVIO,
  OPCOES_TIPO_FICHA,
} from '../../../core/_models'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'

/** Só dígitos — os filtros de atendimento são por identificador. */
const somenteDigitos = (valor: string) => valor.replace(/\D/g, '')

/** Página de opções dos seletores; `selectAll` pede a lista inteira. */
const paginacao = (selectAll: boolean, page: number) => `page=${page}&size=${selectAll ? 2000 : 20}`

/**
 * Filtros da listagem de envios ao PEC: empresa, unidade, tipo de ficha, status, período, paciente e
 * atendimento.
 *
 * Todos opcionais e combináveis — cada mudança reescreve `state.params` inteiro a partir do estado
 * local dos controles, então nunca sobra parâmetro de um filtro que o usuário já limpou. O spread de
 * `initialQueryState` zera a paginação a cada mudança, senão o usuário filtraria estando na página 3
 * e veria vazio.
 *
 * O filtro de empresa **não** é barreira de tenant: quem decide as linhas visíveis é o backend, pelo
 * usuário autenticado. Aqui ele só estreita o que já está autorizado — e as opções vêm de
 * `GET /empresas`, que o próprio backend escopa (super vê todas, os demais só as do vínculo).
 */
export const EsusEnvioListHeader = () => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { currentUser } = useAuth()
  const total = response?.totalElements ?? 0

  const [empresa, setEmpresa] = useState<IOption<number> | null>(null)
  const [unidade, setUnidade] = useState<IOption<number> | null>(null)
  const [status, setStatus] = useState<IOption<EsusEnvioStatus> | null>(null)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [paciente, setPaciente] = useState<IOption<number> | null>(null)
  const [atendimentoId, setAtendimentoId] = useState('')
  const [tipoFicha, setTipoFicha] = useState<IOption<EsusEnvioTipoFicha> | null>(null)

  /**
   * Empresa que ancora os filtros de unidade e de paciente: a escolhida no filtro ou, quando o
   * usuário tem um único vínculo, a dele. Sem âncora (super, ou vínculo múltiplo sem filtro) os dois
   * seletores caem nas listagens globais, que o backend já escopa por autorização.
   */
  const empresaFoco = useMemo(() => {
    if (empresa?.value) return Number(empresa.value)
    const vinculos = (currentUser?.empresas || []).filter((item) => item?.id != null)
    return vinculos.length === 1 ? Number(vinculos[0].id) : undefined
  }, [empresa, currentUser])

  const aplicar = useCallback(
    (filtros: EsusEnvioFiltros) => updateState({ params: filtros, ...initialQueryState }),
    [updateState]
  )

  const filtrosAtuais = useCallback(
    (parcial: Partial<EsusEnvioFiltros>): EsusEnvioFiltros => ({
      empresaId: empresa?.value,
      unidadeId: unidade?.value,
      status: status?.value,
      dataInicio: dataInicio || undefined,
      dataFim: dataFim || undefined,
      pacienteId: paciente?.value,
      atendimentoId: atendimentoId ? Number(atendimentoId) : undefined,
      tipoFicha: tipoFicha?.value,
      ...parcial,
    }),
    [empresa, unidade, status, dataInicio, dataFim, paciente, atendimentoId, tipoFicha]
  )

  /**
   * Unidades: com empresa em foco, o caminho de sempre (`idEmpresa` na consulta). Sem foco, a
   * listagem global — `GET /unidades` já devolve apenas as unidades das empresas autorizadas.
   */
  const buscarUnidades = useCallback(
    async (nome: string, selectAll: boolean, _extra?: Object, page = 0) => {
      if (empresaFoco) {
        return OptionsHelper.getOptionsUnidades(nome, selectAll, { empresaId: empresaFoco }, page)
      }
      const busca = nome ? `&search=nome~*${nome}*` : ''
      const resposta = await getUnidades(`${paginacao(selectAll, page)}${busca}&sort=nome`)
      const options: IOption<number>[] = (resposta?.data || [])
        .filter((item) => item.id != null)
        .map((item) => ({ label: item.nome || `Unidade ${item.id}`, value: Number(item.id) }))
      return { options, hasMore: !resposta?.last }
    },
    [empresaFoco]
  )

  /**
   * Pacientes: mesma lógica de `buscarUnidades`, com uma diferença: **sem** `&sort=nome`. Ao
   * contrário de `Unidade`, `nome` não é coluna de `Paciente` — vem de `pessoa.nome` via projeção
   * (`PacienteBase.getNome()`, `@Value("#{target.pessoa.nome}")`), e a query genérica
   * `findByEmpresa(empresa, pageable, Class<T>)`/`findAll(pageable, Class<T>)` aplica o `Sort` do
   * `Pageable` direto sobre a entidade, não sobre a projeção — `sort=nome` vira
   * `order by p.nome` e quebra com `UnknownPathException` (reproduzido em produção). A lista vem
   * na ordem padrão do backend (id).
   */
  const buscarPacientes = useCallback(
    async (nome: string, selectAll: boolean, _extra?: Object, page = 0) => {
      const busca = nome ? `&search=nome~*${nome}*` : ''
      const consulta = `${paginacao(selectAll, page)}${busca}`
      const resposta = empresaFoco
        ? await getPacientesEmpresa(String(empresaFoco), consulta)
        : await getPacientes(consulta)
      const options: IOption<number>[] = (resposta?.data || [])
        .filter((item) => item.id != null)
        .map((item) => ({ label: item.nome || `Paciente ${item.id}`, value: Number(item.id) }))
      return { options, hasMore: !resposta?.last }
    },
    [empresaFoco]
  )

  const limpar = () => {
    setEmpresa(null)
    setUnidade(null)
    setStatus(null)
    setDataInicio('')
    setDataFim('')
    setPaciente(null)
    setAtendimentoId('')
    setTipoFicha(null)
    updateState({ params: {}, ...initialQueryState })
  }

  /**
   * Trocar (ou limpar) a empresa zera unidade e paciente: os dois são identificadores de um tenant e
   * não fazem sentido combinados com outra empresa. Status, período e atendimento são preservados.
   */
  const trocarEmpresa = (opcao: IOption<number> | null) => {
    setEmpresa(opcao)
    setUnidade(null)
    setPaciente(null)
    aplicar(
      filtrosAtuais({
        empresaId: opcao?.value != null ? Number(opcao.value) : undefined,
        unidadeId: undefined,
        pacienteId: undefined,
      })
    )
  }

  // Chips da faixa "Filtros ativos": cada × limpa só o seu filtro, pelo mesmo caminho do controle
  // correspondente (empresa via `trocarEmpresa`, que também zera unidade e paciente).
  const activeFilters: ActiveFilter[] = [
    empresa && {
      key: 'empresa',
      label: 'Instituição',
      value: empresa.label,
      onRemove: () => trocarEmpresa(null),
    },
    unidade && {
      key: 'unidade',
      label: 'Unidade',
      value: unidade.label,
      onRemove: () => {
        setUnidade(null)
        aplicar(filtrosAtuais({ unidadeId: undefined }))
      },
    },
    paciente && {
      key: 'paciente',
      label: 'Paciente',
      value: paciente.label,
      onRemove: () => {
        setPaciente(null)
        aplicar(filtrosAtuais({ pacienteId: undefined }))
      },
    },
    tipoFicha && {
      key: 'tipoFicha',
      label: 'Tipo de ficha',
      value: tipoFicha.label,
      onRemove: () => {
        setTipoFicha(null)
        aplicar(filtrosAtuais({ tipoFicha: undefined }))
      },
    },
    status && {
      key: 'status',
      label: 'Status',
      value: status.label,
      onRemove: () => {
        setStatus(null)
        aplicar(filtrosAtuais({ status: undefined }))
      },
    },
    dataInicio && {
      key: 'dataInicio',
      label: 'Atendimento de',
      value: formatDateToPtBr(dataInicio),
      onRemove: () => {
        setDataInicio('')
        aplicar(filtrosAtuais({ dataInicio: undefined }))
      },
    },
    dataFim && {
      key: 'dataFim',
      label: 'Atendimento até',
      value: formatDateToPtBr(dataFim),
      onRemove: () => {
        setDataFim('')
        aplicar(filtrosAtuais({ dataFim: undefined }))
      },
    },
    atendimentoId && {
      key: 'atendimentoId',
      label: 'Atendimento',
      value: `nº ${atendimentoId}`,
      onRemove: () => {
        setAtendimentoId('')
        aplicar(filtrosAtuais({ atendimentoId: undefined }))
      },
    },
  ].filter((filtro): filtro is ActiveFilter => Boolean(filtro))

  const moreFiltersActiveCount = [tipoFicha, status, dataInicio, dataFim, atendimentoId].filter(
    Boolean
  ).length

  return (
    <div className='card-header border-0 pt-6'>
      <TableFilters
        moreFiltersActiveCount={moreFiltersActiveCount}
        moreFilters={
          <>
            <TableFilter label='Tipo de ficha' htmlFor='filtro-tipo-ficha'>
              <CustomSelect
                inputId='filtro-tipo-ficha'
                options={OPCOES_TIPO_FICHA}
                value={tipoFicha ?? undefined}
                placeholder='Todos os tipos'
                isClearable
                onChange={(opcao: IOption<EsusEnvioTipoFicha> | null) => {
                  setTipoFicha(opcao)
                  // Sem seleção sai como `undefined`: `buildQueryString` descarta a chave e a lista
                  // volta a trazer os três tipos de ficha (a de procedimentos entrou na F3).
                  aplicar(filtrosAtuais({ tipoFicha: opcao?.value }))
                }}
              />
            </TableFilter>

            <TableFilter label='Status'>
              <CustomSelect
                options={OPCOES_STATUS_ENVIO}
                value={status ?? undefined}
                placeholder='Todos'
                isClearable
                onChange={(opcao: IOption<EsusEnvioStatus> | null) => {
                  setStatus(opcao)
                  aplicar(filtrosAtuais({ status: opcao?.value }))
                }}
              />
            </TableFilter>

            {/*
              Período é sobre a DATA DO ATENDIMENTO, não a data de envio: `dhEnvio` é nulo justamente
              nos registros em PENDENTE/ERRO, que são o motivo desta tela existir.
            */}
            <TableFilter
              label='Atendimento de'
              hint='Filtra pela data do atendimento, não pela data de envio.'
            >
              <DateInput
                placeholder='Data inicial'
                value={dataInicio}
                onChange={(e) => {
                  setDataInicio(e.target.value)
                  aplicar(filtrosAtuais({ dataInicio: e.target.value || undefined }))
                }}
              />
            </TableFilter>

            <TableFilter
              label='Atendimento até'
              hint='Filtra pela data do atendimento, não pela data de envio.'
            >
              <DateInput
                placeholder='Data final'
                value={dataFim}
                onChange={(e) => {
                  setDataFim(e.target.value)
                  aplicar(filtrosAtuais({ dataFim: e.target.value || undefined }))
                }}
              />
            </TableFilter>

            <TableFilter label='Atendimento'>
              <TextInput
                placeholder='Nº do atendimento'
                value={atendimentoId}
                onChange={(e) => {
                  const valor = somenteDigitos(e.target.value)
                  setAtendimentoId(valor)
                  aplicar(filtrosAtuais({ atendimentoId: valor ? Number(valor) : undefined }))
                }}
              />
            </TableFilter>
          </>
        }
        footer={
          <ActiveFiltersBar
            filters={activeFilters}
            onClearAll={limpar}
            summary={
              isLoading
                ? 'Buscando…'
                : `${total} ${total === 1 ? 'envio encontrado' : 'envios encontrados'}`
            }
          />
        }
      >
        <TableFilter
          label='Instituição'
          hint='Apenas as instituições às quais você tem acesso. Sem seleção, a lista traz todas elas.'
          wide
        >
          <CustomSelect
            value={empresa ?? undefined}
            placeholder='Todas as instituições'
            isClearable
            fetchOptions={OptionsHelper.getOptionsEmpresas}
            // Empresa desativada continua tendo envio em erro na lista combinada; escondê-la aqui
            // deixaria uma linha visível sem filtro capaz de estreitá-la.
            fetchOptionsParams={{ onlyActive: false }}
            onChange={trocarEmpresa}
          />
        </TableFilter>
        <TableFilter label='Unidade'>
          <CustomSelect
            value={unidade ?? undefined}
            placeholder='Todas as unidades'
            isClearable
            fetchOptions={buscarUnidades}
            fetchOptionsParams={{ empresaId: empresaFoco }}
            onChange={(opcao: IOption<number> | null) => {
              setUnidade(opcao)
              aplicar(filtrosAtuais({ unidadeId: opcao?.value }))
            }}
          />
        </TableFilter>
        <TableFilter label='Paciente'>
          <CustomSelect
            value={paciente ?? undefined}
            placeholder='Todos os pacientes'
            isClearable
            fetchOptions={buscarPacientes}
            fetchOptionsParams={{ empresaId: empresaFoco }}
            onChange={(opcao: IOption<number> | null) => {
              setPaciente(opcao)
              aplicar(filtrosAtuais({ pacienteId: opcao?.value }))
            }}
          />
        </TableFilter>
      </TableFilters>
    </div>
  )
}
